import React, { useEffect, useState } from "react";
import CalendarAssorts from "../Assorts";
import CalendarExerciseChoice from "../ExerciseChoice";
import useCalendarSale from "../hooks/useCalendarSale";
import { Exercise } from "../../../models/Exercise";
import { format } from "date-fns";
import { utcToZonedTime } from "date-fns-tz";
import { useTranslation } from "react-i18next";
import useAssortLimits from "../../Universal/hooks/useAssortLimits";
import getId from "../../../../utils/getId";
import FlowPanel from "./shared/FlowPanel";

interface IProps {
  exerciseName?: string;
  exercise: Exercise[];
  refreshCapacityAfterAddTicket: () => void;
  joinToExerciseByPass: (exerciseIds: string, assignType: boolean, passId: number) => void;
  alertCalendar: string;
  onTimeSelect?: () => void;
  /** Wybrana data w czytelnej formie – w podsumowaniu nad sumą. */
  dateLabel: string;
  /** Powrót na początek ścieżki (po „Kup kolejne bilety"). */
  onBuyMore?: () => void;
}

const NO_SLOT_SELECTED = 99;

/**
 * Kolumny 2 i 3 ścieżki zakupu: godzina wejścia + bilety.
 */
const CalendarMultiExercise: React.FC<IProps> = ({
  exercise,
  refreshCapacityAfterAddTicket,
  joinToExerciseByPass,
  alertCalendar,
  onTimeSelect,
  dateLabel,
  onBuyMore,
}: IProps) => {
  const { t } = useTranslation();
  const {
    getTicketsByExercise,
    tickets,
    ticketLimit,
    ticketAccess,
    chooseTime,
    getDetailsAboutExercise,
    exerciseDetails,
  } = useCalendarSale();
  const { limits, isFallback, isLoading: isLimitsLoading, getLimitForAssort, fetchLimits } = useAssortLimits();

  const [whichClicked, setClicked] = useState(NO_SLOT_SELECTED);

  // Synchronizacja zaznaczonej godziny z wybranym terminem (po odświeżeniu listy godzin).
  // chooseTime === 0 = jeszcze nic nie wybrano – nie nadpisujemy zaznaczenia ustawionego kliknięciem.
  useEffect(() => {
    if (chooseTime && chooseTime > 0) {
      const matchingIndex = exercise.findIndex((ex) => ex.startTime === chooseTime);
      if (matchingIndex !== -1) {
        setClicked(matchingIndex);

        // Pobierz limity dla wybranego zajęcia
        const matchingExercise = exercise[matchingIndex];
        fetchLimits(`${matchingExercise.objectDb},${matchingExercise.objectId}`);
        return;
      }
      setClicked(NO_SLOT_SELECTED);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [exercise, chooseTime]);

  const getDetailsAboutClickedExercise = (exerciseId: number) => {
    getDetailsAboutExercise(exerciseId);
  };

  const isTimeSelected = whichClicked !== NO_SLOT_SELECTED;

  // Oblicz globalLimit na podstawie limitów asortymentowych dla wybranego zajęcia
  const selectedExercise = isTimeSelected ? exercise[whichClicked] : undefined;
  const availableCapacity = selectedExercise ? selectedExercise.capacity - selectedExercise.reserved : ticketLimit;

  const visibleAssortIds = new Set(tickets.map((ticket) => getId(ticket)));

  const hardLimited = limits.filter((limit) => {
    const limitAssortId = getId(limit.assortIds);
    return visibleAssortIds.has(limitAssortId) && limit.maxQuantity !== null && limit.maxQuantity !== 0;
  });

  const limitedRemainingTotal = hardLimited.reduce((sum, limit) => {
    if (limit.remainingQuantity === null || limit.remainingQuantity === undefined) return sum;
    return sum + limit.remainingQuantity;
  }, 0);

  const hasHardLimits = hardLimited.length > 0;
  const shouldFallbackToCapacity = !isFallback && hasHardLimits && limitedRemainingTotal > availableCapacity;

  const globalLimit = isFallback
    ? availableCapacity
    : !hasHardLimits || shouldFallbackToCapacity
      ? availableCapacity
      : Math.min(availableCapacity, limitedRemainingTotal);

  const exerciseGroupCompositeId = selectedExercise ? getId(selectedExercise.exerciseGroup) : undefined;

  const limitsProps = {
    getLimitForAssort,
    isFallback,
    isLimitsLoading,
    globalLimitOverride: globalLimit,
    exerciseGroupCompositeId,
  };

  const timeLabel = isTimeSelected && chooseTime > 0 ? format(utcToZonedTime(chooseTime, "Europe/Warsaw"), "HH:mm") : "";

  return (
    <>
      <FlowPanel
        step={2}
        title={t("godzina_wejscia")}
        value={timeLabel}
        done={isTimeSelected}
        className="flow-col--hours"
      >
        <CalendarExerciseChoice
          getDetailsAboutClickedExercise={getDetailsAboutClickedExercise}
          exercise={exercise}
          getTicketsByExercise={getTicketsByExercise}
          handleWhichClicked={setClicked}
          whichClicked={whichClicked}
          onTimeSelect={onTimeSelect}
        />
      </FlowPanel>

      {isTimeSelected ? (
        <FlowPanel step={3} title={t("wybierz_rodzaj_i_ilosc_biletow")} className="flow-col--tickets">
          <CalendarAssorts
            alertCalendar={alertCalendar}
            exerciseDetails={exerciseDetails}
            joinToExerciseByPass={joinToExerciseByPass}
            tickets={tickets}
            ticketAccess={ticketAccess}
            ticketLimit={ticketLimit}
            refreshCapacityAfterAddTicket={refreshCapacityAfterAddTicket}
            summaryContext={{ date: dateLabel, time: timeLabel || undefined }}
            onBuyMore={onBuyMore}
            {...limitsProps}
          />
        </FlowPanel>
      ) : (
        <FlowPanel
          step={3}
          title={t("wybierz_rodzaj_i_ilosc_biletow")}
          locked
          lockedMessage={t("wybierz_godzine")}
          className="flow-col--tickets"
        />
      )}
    </>
  );
};

export default CalendarMultiExercise;
